import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = '/logo_transparent.png';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <header className="border-b border-slate-200 py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="relative cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="RentalHub" className="h-10 object-contain" />
            <span className="absolute text-[4px] font-semibold text-slate-400 select-none" style={{ top: '28%', right: '12%' }}>TM</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="privacy-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Back to Home
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-2" data-testid="privacy-heading">Privacy Policy</h1>
        <p className="text-sm text-slate-400 mb-10">Last updated: April 14, 2026</p>

        <div className="prose prose-slate prose-sm max-w-none space-y-8 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">1. Introduction</h2>
            <p>RentalHub ("we", "our", or "us") is committed to protecting the privacy of our users ("you" or "your"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our property management platform, including our website, mobile applications, and related services (collectively, the "Service").</p>
            <p>By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access or use the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">2. Information We Collect</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">2.1 Personal Information</h3>
            <p>When you create an account or use our Service, we may collect the following personal information:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Full name and contact details (email address, phone number)</li>
              <li>Account credentials (email and encrypted password)</li>
              <li>Property and unit information you provide</li>
              <li>Guest information you enter into the system</li>
              <li>Payment information processed through our payment provider (Paystack)</li>
              <li>Communication records within the platform</li>
            </ul>

            <h3 className="text-base font-semibold text-slate-800 mb-2 mt-4">2.2 Automatically Collected Information</h3>
            <p>When you access the Service, we automatically collect certain information, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Device information (browser type, operating system, device identifiers)</li>
              <li>Log data (IP address, access times, pages viewed, referring URLs)</li>
              <li>Usage patterns and feature interactions</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="text-base font-semibold text-slate-800 mb-2 mt-4">2.3 Third-Party Data</h3>
            <p>We may receive booking information from third-party platforms (such as Airbnb, Booking.com, and Vrbo) through iCal calendar synchronisation. This data is used solely to display and manage your bookings within our platform.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>To provide, maintain, and improve the Service</li>
              <li>To process transactions and send related information</li>
              <li>To send administrative messages, updates, and security alerts</li>
              <li>To respond to your comments, questions, and support requests</li>
              <li>To monitor and analyse usage patterns and trends</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations and enforce our terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">4. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information to third parties. We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Service Providers:</strong> We share data with trusted third-party service providers who assist us in operating the Service, including payment processors (Paystack), cloud hosting providers, and analytics services.</li>
              <li><strong>Team Members:</strong> Information may be shared with other users within your organisation based on role-based access controls that you configure.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, regulation, legal process, or governmental request.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your information, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Encryption of data in transit using TLS/SSL</li>
              <li>Secure password hashing using bcrypt</li>
              <li>JWT-based authentication with token expiration</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and role-based permissions</li>
            </ul>
            <p>While we strive to protect your information, no method of transmission over the Internet or method of electronic storage is 100% secure. We cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">6. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide you with the Service. We may also retain certain information as required by law, to resolve disputes, enforce agreements, or for legitimate business purposes.</p>
            <p>When you delete your account, we will delete or anonymise your personal information within 30 days, except where we are required to retain it for legal or regulatory purposes.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your personal information</li>
            </ul>
            <p>To exercise any of these rights, please contact us at privacy@rentalhub.app.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">8. Cookies</h2>
            <p>We use cookies and similar technologies to enhance your experience, analyse usage, and assist in our marketing efforts. You can control cookie preferences through your browser settings. Essential cookies required for the Service to function cannot be disabled.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">9. Children's Privacy</h2>
            <p>The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it promptly.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after any changes constitutes your acceptance of the revised policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">11. Contact Us</h2>
            <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
            <p className="font-medium">RentalHub<br />Email: privacy@rentalhub.app</p>
          </section>
        </div>
      </main>

      <footer className="py-8 px-4 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} RentalHub. All rights reserved.</p>
      </footer>
    </div>
  );
}
